import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import React, { useState } from 'react';
import { AppRoutes } from './routes/AppRoutes';
import { IntlProvider } from 'react-intl';
import { getMessagesForLocale } from './i18n/i18n';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { VehicleSidebar } from './pages/onboarding/components/sidebar/VehicleSidebar';
import { Header } from './components/header/Header';
import { getUser } from './login/loginSlice';
import { useAppSelector } from './state/hooks';
import { TransactionSidebar } from './pages/transactions/components/sidebar/TransactionSidebar';
import { VehicleAssetTree } from './pages/transactions/components/assetTree/VehicleAssetTree';
import { SupportDialog } from './components/dialog/supportDialog/SupportDialog';
import { UserManualDialog } from './components/dialog/userManualDialog/UserManualDialog';
import {
  EVENT_USER_LANGUAGE_CHANGED,
  EVENT_USER_LOGGED_OUT,
} from '@rio-cloud/rio-user-menu-component';
import { clearDismissedCallouts } from './pages/overview/components/yourInsights/dismissedCalloutsStorage';
import {
  DEFAULT_LOCALE,
  useLocale,
  UserMenuLanguageChangedEvent,
} from './i18n/locale';
import { BookingSidebar } from './pages/bookings/components/sidebar/BookingSidebar';
import { usePaymentMethodUpdateNotification } from './components/usePaymentMethodUpdateNotification';
import useEffectOnce from '@rio-cloud/rio-uikit/useEffectOnce';
import { WhatsNew } from './components/WhatsNew';
import { appConfiguration } from './configuration/appConfiguration';
import { ScreenshotRegions } from './components/screenshot/screenshotRegions';

export const App: React.FC = () => {
  const { locale, setLocale } = useLocale();
  const [messages, setMessages] = useState<Record<string, string>>();
  const user = useAppSelector(getUser);

  usePaymentMethodUpdateNotification();

  const loadTranslations = async (language: string) => {
    let data;
    try {
      data = await getMessagesForLocale(language);
      setLocale(language);
    } catch {
      data = await getMessagesForLocale(DEFAULT_LOCALE);
    }
    setMessages(data);
  };

  const handleLanguageChanged = (event: Event) => {
    const language = (event as UserMenuLanguageChangedEvent).detail?.language;
    loadTranslations(language);
  };

  useEffectOnce(() => {
    loadTranslations(user.locale);
    document.addEventListener(
      EVENT_USER_LANGUAGE_CHANGED,
      handleLanguageChanged,
    );

    document.addEventListener(EVENT_USER_LOGGED_OUT, clearDismissedCallouts);
  });

  return (
    messages && (
      <IntlProvider
        defaultLocale={DEFAULT_LOCALE}
        locale={locale}
        messages={messages}
      >
        <ScreenshotRegions />
        <ApplicationLayout>
          <ApplicationLayout.Header>
            <Header />
          </ApplicationLayout.Header>
          <VehicleSidebar />
          <VehicleAssetTree />
          <TransactionSidebar />
          <BookingSidebar />
          <ApplicationLayout.Body>
            {appConfiguration.whatsNew.articleId && (
              <WhatsNew
                articleId={appConfiguration.whatsNew.articleId}
                link={appConfiguration.whatsNew.link}
              />
            )}
            <SupportDialog />
            <UserManualDialog />
            <NotificationsContainer />
            <AppRoutes />
          </ApplicationLayout.Body>
        </ApplicationLayout>
      </IntlProvider>
    )
  );
};
